import { ArrowUpRightIcon, ClockIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import { Checkbox } from "@/components/ui/checkbox";

import { agenticScoreToBucket } from "@/lib/agenticScore";
import { MS_PER_DAY } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { CommsToSendCandidacy } from "@/types";

// Overdue past next-action by less than a day doesn't get a badge — only once
// a full day (floor) has elapsed. An untimed shortlist candidacy has no due
// date to be late against, so it never badges.
const daysLate = (nextActionAt: string | null): number =>
  nextActionAt === null
    ? 0
    : Math.floor((Date.now() - new Date(nextActionAt).getTime()) / MS_PER_DAY);

// Human led communications that need follow-up after 24-hours
// These show up in tasks, but they're not real tasks,
// so they'll auto-disappear when call or text is detected for that canidacy
export const DashboardCommsToSendSection = ({
  candidacies,
  variant = "compact",
}: {
  candidacies: CommsToSendCandidacy[];
  variant?: "compact" | "comfortable";
}) => {
  /* Helpers */
  const isComfortable = variant === "comfortable";

  /* Render */
  // Compact matches the other category-section headers (plain slate-500, no
  // band); comfortable keeps its banded header.
  const renderHeader = () => (
    <Row
      className={cn(
        "items-center gap-1.5",
        isComfortable
          ? "border-b border-lever-border-light bg-lever-surface-default-on-elevation/80 px-4 py-2"
          : "py-1",
      )}
    >
      <Text
        className={cn(
          "font-semibold",
          isComfortable
            ? "text-xs text-lever-text-footnote"
            : "text-[11px] text-lever-text-label",
        )}
      >
        Comms to Send
        <span
          className={cn(
            "italic text-lever-text-label",
            isComfortable ? "text-[11px]" : "text-[10px]",
          )}
        >
          {" "}
          - Automatically disappear once you reach out
        </span>
      </Text>
    </Row>
  );

  const renderRows = () => {
    if (candidacies.length === 0) {
      return (
        <Text
          className={cn(
            "italic text-lever-text-label",
            isComfortable ? "px-4 py-2 text-sm" : "text-[11px]",
          )}
        >
          No comms to send :)
        </Text>
      );
    }
    return (
      <Column className={isComfortable ? "px-4 py-1" : "gap-0.5"}>
        {candidacies.map((candidacy) => (
          <DashboardCommsToSendRow
            key={candidacy.candidacyId}
            candidacy={candidacy}
            variant={variant}
          />
        ))}
      </Column>
    );
  };

  const render = () => (
    <Column>
      {renderHeader()}
      {renderRows()}
    </Column>
  );

  return render();
};

const DashboardCommsToSendRow = ({
  candidacy,
  variant,
}: {
  candidacy: CommsToSendCandidacy;
  variant: "compact" | "comfortable";
}) => {
  /* Helpers */
  const isComfortable = variant === "comfortable";
  const gap = isComfortable ? "gap-2.5" : "gap-1.5";
  const href = getCandidacyLink({
    candidacyId: candidacy.candidacyId,
    companyId: candidacy.companyId,
    jobId: candidacy.jobId,
  });
  const bucket = agenticScoreToBucket(candidacy.agenticScore);
  const late = daysLate(candidacy.nextActionAt);

  /* Handlers */
  const handleOpen = () => {
    mixpanelUtils.trackButtonClick(
      "Open comms-to-send candidate",
      MIXPANEL_SCREENS.DASHBOARD,
      { candidacy_id: candidacy.candidacyId },
    );
  };

  /* Render */
  const render = () => (
    <Row
      className={cn(
        "group items-center rounded-lg hover:bg-lever-surface-default-on-elevation",
        gap,
        isComfortable ? "px-2 py-2" : "px-1.5 py-0.5",
      )}
    >
      <Checkbox
        checked={false}
        className={cn(
          "shrink-0 rounded-full opacity-50",
          isComfortable ? "h-4 w-4" : "h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3",
        )}
        disabled
      />
      <a
        className={cn("flex min-w-0 flex-1 items-center", gap)}
        href={href}
        onClick={handleOpen}
        rel="noopener noreferrer"
        target="_blank"
      >
        <AgenticScoreChip bucket={bucket} variant="xs" />
        <Text
          className={cn(
            "shrink-0 text-left leading-snug group-hover:text-lever-text-information",
            isComfortable ? "text-sm" : "text-[11px]",
          )}
        >
          {candidacy.candidateName}
        </Text>
        <Text
          truncate
          variant="secondary"
          className={cn(
            "min-w-0 flex-1 text-left leading-snug group-hover:text-lever-text-information",
            isComfortable ? "text-sm" : "text-[11px]",
          )}
        >
          — {candidacy.jobTitle} at {candidacy.companyName}
        </Text>
        <ArrowUpRightIcon
          className={cn(
            "hidden shrink-0 text-lever-text-information group-hover:block",
            isComfortable ? "h-4 w-4" : "h-3.5 w-3.5",
          )}
        />
        {late >= 1 ? <DaysLateBadge days={late} /> : null}
      </a>
    </Row>
  );

  return render();
};

// Far-right pill showing how many full days past nextActionAt this candidacy
// has gone unresolved.
const DaysLateBadge = ({ days }: { days: number }) => {
  const render = () => (
    <Row className="inline-flex shrink-0 items-center gap-1 rounded-full border border-lever-border-error bg-lever-surface-error px-2 py-0.5">
      <ClockIcon className="h-3 w-3 shrink-0 text-lever-text-error" />
      <Text className="font-lever-suisse text-[11px] font-medium text-lever-text-error">
        {days.toString()}d late
      </Text>
    </Row>
  );

  return render();
};
